/**
 * W01 — Date formatting helpers (framework-free, domain layer).
 * ISO strings from the API degrade gracefully for malformed input.
 */
import { format, isValid, parseISO } from 'date-fns';
import { toBengaliNumerals } from './currency';
import type { SupportedLocale } from './currency';

export function formatDate(iso: string | null | undefined, locale: SupportedLocale = 'en'): string {
  if (!iso) return '—';
  const parsed = parseISO(iso);
  if (!isValid(parsed)) return '—';
  const out = format(parsed, 'dd MMM yyyy');
  return locale === 'bn' ? toBengaliNumerals(out) : out;
}

export function formatDateTime(iso: string | null | undefined, locale: SupportedLocale = 'en'): string {
  if (!iso) return '—';
  const parsed = parseISO(iso);
  if (!isValid(parsed)) return '—';
  const out = format(parsed, 'dd MMM yyyy, HH:mm');
  return locale === 'bn' ? toBengaliNumerals(out) : out;
}

/** Whole days between now and an ISO date; negative when past. null-safe. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = parseISO(iso);
  if (!isValid(target)) return null;
  const ms = target.getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
}

const MONTHS: Record<SupportedLocale, { long: string[]; short: string[] }> = {
  en: {
    long: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  },
  bn: {
    long: ['জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন', 'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'],
    short: ['জানু', 'ফেব্রু', 'মার্চ', 'এপ্রিল', 'মে', 'জুন', 'জুলাই', 'আগস্ট', 'সেপ্টে', 'অক্টো', 'নভে', 'ডিসে'],
  },
};

const DHAKA_OFFSET_MS = 6 * 3_600_000;

function localizeDigits(value: string, locale: SupportedLocale): string {
  return locale === 'bn' ? toBengaliNumerals(value) : value;
}

/** `YYYY-MM` → "October 2026" / "অক্টোবর ২০২৬" (`short` → "Oct 2026"). Malformed keys give "—". */
export function formatMonthKey(key: string, locale: SupportedLocale = 'en', style: 'long' | 'short' = 'long'): string {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(key);
  if (!match) return '—';
  return localizeDigits(`${MONTHS[locale][style][Number(match[2]) - 1]} ${match[1]}`, locale);
}

/** Month name only (chart axis), e.g. "Oct" / "অক্টো". */
export function formatMonthName(key: string, locale: SupportedLocale = 'en'): string {
  const match = /^\d{4}-(0[1-9]|1[0-2])$/.exec(key);
  return match ? MONTHS[locale].short[Number(match[1]) - 1] : '—';
}

/** Calendar date on the Asia/Dhaka clock with locale month names: "15 Oct 2026" / "১৫ অক্টো ২০২৬". */
export function formatDhakaDate(iso: string | null | undefined, locale: SupportedLocale = 'en'): string {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return '—';
  const d = new Date(ms + DHAKA_OFFSET_MS);
  const month = MONTHS[locale].short[d.getUTCMonth()];
  return localizeDigits(`${String(d.getUTCDate()).padStart(2, '0')} ${month} ${d.getUTCFullYear()}`, locale);
}

/** Plain number with locale digits ("14" → "১৪"). */
export function formatNumber(value: number, locale: SupportedLocale = 'en'): string {
  const safe = Number.isFinite(value) ? String(value) : '0';
  return localizeDigits(safe, locale);
}

/** "12.5%" / "১২.৫%" — null-safe via the caller (pass a finite number). */
export function formatPercent(value: number, locale: SupportedLocale = 'en'): string {
  return `${formatNumber(value, locale)}%`;
}
